import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { Button, ButtonColor, ButtonTypes } from "../basic/button";
import { ModalOptions } from "../basic/modal";
import style from "./style.module.scss";

type Modal = ModalOptions.Props;
export interface ModalContextType {
	modal: Modal;
	toggleModal: (show?: boolean) => void;
	clearModal: () => void;
	updateModal: (props: Partial<Modal>) => void;
	setModal: (props: Partial<Modal>) => void;
	createConfirmation: (props: ConfirmationModalProps) => void;
}

export interface ConfirmationModalProps extends Partial<Modal> {
	text: string;
	headerText?: string;
	confirmText?: string;
	cancelText?: string;
	showCancel?: boolean;
	onConfirm?: () => Promise<void>;
	onCancel?: () => Promise<void>;
}

const defaultContextValues = {
	modal: {} as Modal,
} as ModalContextType;
interface ModalContextProps {
	children: ReactNode;
}

export const ModalContext = createContext<ModalContextType>(defaultContextValues);
export const useModalContext = () => useContext(ModalContext);
export function ModalProvider(props: ModalContextProps) {
	const { children } = props;
	const modalUpdate = useModalUpdate();

	return <ModalContext.Provider value={modalUpdate}>{children}</ModalContext.Provider>;
}

export function useModalUpdate() {
	const [modal, sModal] = useState({} as Modal);
	const setModal = useCallback((m: Partial<Modal>) => sModal(m as Modal), [sModal]);
	const updateModal = useCallback(
		(m: Partial<Modal>) => {
			setModal({ ...modal, ...m });
		},
		[setModal, modal],
	);
	const clearModal = useCallback(() => {
		setModal({} as Modal);
	}, [setModal]);
	const toggleModal = useCallback(
		(show?: boolean) => {
			updateModal({ visible: show ?? !modal.visible });
		},
		[modal.visible, updateModal],
	);
	const createConfirmation = useCallback(createConfirmationModal, [setModal, toggleModal]);
	return useMemo(
		() => ({
			modal,
			setModal,
			updateModal,
			clearModal,
			toggleModal,
			createConfirmation,
		}),
		[modal, setModal, updateModal, clearModal, toggleModal, createConfirmation],
	);

	function createConfirmationModal(props: ConfirmationModalProps) {
		const { text, headerText, confirmText, cancelText, showClose, showCancel = true, onCancel, onConfirm } = props;

		function renderHeader() {
			return <>{headerText ?? "Please Confirm"}</>;
		}

		function renderBody() {
			async function confirm() {
				if (onConfirm) await onConfirm();
				toggleModal(false);
			}

			async function cancel() {
				if (onCancel) await onCancel();
				toggleModal(false);
			}

			return (
				<div className={style.body}>
					<h3>{text}</h3>
					<div className={style.buttons}>
						<Button onClick={confirm} color={ButtonColor.DarkGreen}>
							{confirmText ?? "Confirm"}
						</Button>
						{showCancel && (
							<Button onClick={cancel} types={[ButtonTypes.Danger]}>
								{cancelText ?? "Cancel"}
							</Button>
						)}
					</div>
				</div>
			);
		}

		setModal({
			header: renderHeader(),
			body: renderBody(),
			visible: true,
			showClose: showClose ?? true,
			closeFunction: clearModal,
		});
	}
}
